import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, User, Phone, Mail, Calendar, Link as LinkIcon, Pencil, Trash2, X, Check, FileText, BarChart3, Users, Plus, ArrowRight } from 'lucide-react';
import { Client, Contact, Collab, clientsRef, collabsRef, getDocuments, updateDocument, deleteDocument, BUSINESS_SECTORS } from '../lib/firebase';
import { Dialog } from '@headlessui/react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [client, setClient] = useState<Client | null>(null);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Client>>({
    brand_name: '',
    contacts: [],
    sector: { main: '', sub: '' },
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID client non spécifié');
        setIsLoading(false);
        return;
      }

      // Charger les données du client
      const clientDoc = await getDoc(doc(db, 'clients', id));
      if (!clientDoc.exists()) {
        setError('Client non trouvé');
        setIsLoading(false);
        return;
      }

      const clientData = { id: clientDoc.id, ...clientDoc.data() } as Client;
      setClient(clientData);
      setFormData({
        brand_name: clientData.brand_name,
        contacts: [...clientData.contacts],
        sector: clientData.sector || { main: '', sub: '' },
        notes: clientData.notes
      });

      // Charger les collaborations liées à ce client
      const collabsData = await getDocuments<Collab>(collabsRef);
      const clientCollabs = collabsData.filter(collab => collab.brand === clientData.brand_name);
      
      // Trier par date (plus récentes en premier)
      clientCollabs.sort((a, b) => {
        const dateA = a.date ? 
          (typeof a.date === 'object' && 'seconds' in a.date) ? 
            a.date.seconds * 1000 : 
            new Date(a.date).getTime() : 
          0;
        
        const dateB = b.date ? 
          (typeof b.date === 'object' && 'seconds' in b.date) ? 
            b.date.seconds * 1000 : 
            new Date(b.date).getTime() : 
          0;
        
        return dateB - dateA;
      });
      
      setCollabs(clientCollabs);
    } catch (error) {
      console.error('Error loading client data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!id) return;

      await updateDocument(clientsRef, id, formData);
      setSuccess('Client mis à jour avec succès');
      await loadData();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating client:', error);
      setError('Erreur lors de la mise à jour du client');
    }
  };

  const handleDelete = async () => {
    try {
      if (!id) return;

      await deleteDocument(clientsRef, id);
      setSuccess('Client supprimé avec succès');
      setTimeout(() => {
        navigate('/app/clients');
      }, 1500);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Erreur lors de la suppression du client');
    }
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), {
        name: '',
        email: '',
        phone: '',
        type: 'brand',
        role: ''
      }]
    }));
  };

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.filter((_, i) => i !== index) || []
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      ) || []
    }));
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    
    // Si c'est une chaîne de caractères (format ISO), la convertir en Date
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return 'Date invalide';
      return parsedDate.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Si c'est un objet avec une propriété 'seconds' (Firebase Timestamp)
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Sinon, traiter comme un objet Date
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.payment_received) return { label: 'Terminée', className: 'bg-green-100 text-green-700 border-green-600' };
    if (campaign.contract_signed) return { label: 'En cours', className: 'bg-yellow-100 text-yellow-700 border-yellow-600' };
    if (campaign.quote_sent) return { label: 'Devis envoyé', className: 'bg-blue-100 text-blue-700 border-blue-600' };
    return { label: 'En attente', className: 'bg-gray-100 text-gray-700 border-gray-600' };
  };

  const getTotalRevenue = () => {
    return collabs.reduce((total, collab) => {
      const collabTotal = collab.influencers?.reduce((sum, inf) => 
        sum + parseFloat(inf.rate || '0'), 0) || 0;
      return total + collabTotal;
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/clients')}
            className="p-2 rounded-lg bg-white hover:bg-gray-50 border border-gray-200"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{client?.brand_name}</h1>
            {client?.sector?.main && (
              <p className="text-gray-600">
                {BUSINESS_SECTORS.find(s => s.value === client.sector.main)?.label}
                {client.sector.sub && (
                  <>
                    {' • '}
                    {BUSINESS_SECTORS
                      .find(s => s.value === client.sector.main)
                      ?.subcategories.find(sub => sub.value === client.sector.sub)?.label}
                  </>
                )}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-200"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Statistiques */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Aperçu</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500">Campagnes</h3>
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{collabs.length}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500">Influenceurs</h3>
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(collabs.flatMap(c => c.influencers.map(i => i.fullName))).size}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-500">CA Total</h3>
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(getTotalRevenue())}</p>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Contacts</h2>
            <div className="space-y-4">
              {client?.contacts.map((contact, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    {contact.type === 'brand' ? (
                      <Building2 className="w-5 h-5 text-indigo-600" />
                    ) : (
                      <User className="w-5 h-5 text-purple-600" />
                    )}
                    <div>
                      <span className="text-gray-900 font-medium">{contact.name}</span>
                      {contact.role && (
                        <span className="text-gray-500 ml-2">• {contact.role}</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 pl-7">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <a href={`mailto:${contact.email}`} className="hover:text-indigo-600">
                        {contact.email}
                      </a>
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <a href={`tel:${contact.phone}`} className="hover:text-indigo-600">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!client?.contacts || client.contacts.length === 0) && (
                <div className="text-center py-6 text-gray-500">
                  Aucun contact enregistré
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {client?.notes && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-gray-700 whitespace-pre-wrap">{client.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Informations */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Date d'ajout</p>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">{formatDate(client?.createdAt)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dernière modification</p>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-gray-900">{formatDate(client?.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Campagnes récentes */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Campagnes récentes</h2>
            <div className="space-y-3">
              {collabs.slice(0, 5).map((collab) => (
                <div 
                  key={collab.id} 
                  className="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:bg-gray-100 cursor-pointer"
                  onClick={() => navigate(`/app/collaborations/${collab.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{collab.name}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCampaignStatus(collab).className}`}>
                      {getCampaignStatus(collab).label}
                    </span> 
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{formatDate(collab.date)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {collab.influencers?.length || 0} influenceur(s)
                    </span>
                    <span className="text-sm font-medium text-indigo-600">
                      {formatCurrency(collab.influencers?.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0) || 0)}
                    </span>
                  </div>
                </div>
              ))}
              {collabs.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  Aucune campagne pour ce client
                </div>
              )}
              {collabs.length > 5 && (
                <button
                  onClick={() => {/* Afficher toutes les campagnes */}}
                  className="w-full flex items-center justify-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 py-2 mt-2"
                >
                  <span>Voir toutes les campagnes ({collabs.length})</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 shadow-lg">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Modifier le client
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="brand_name" className="block text-sm font-medium text-gray-700">
                      Nom
                    </label>
                    <input
                      type="text"
                      id="brand_name"
                      value={formData.brand_name}
                      onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                      className="mt-1 block w-full rounded-lg bg-white border-2 border-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4"
                      placeholder="Nom de la marque ou de l'agence"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secteur principal
                    </label>
                    <select
                      value={formData.sector?.main || ''}
                      onChange={(e) => {
                        const mainSector = e.target.value;
                        setFormData({
                          ...formData,
                          sector: {
                            main: mainSector,
                            sub: ''
                          }
                        });
                      }}
                      className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Sélectionner un secteur</option>
                      {BUSINESS_SECTORS.map((sector) => (
                        <option key={sector.value} value={sector.value}>{sector.label}</option>
                      ))}
                    </select>
                    
                    {formData.sector?.main && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sous-catégorie
                        </label>
                        <select
                          value={formData.sector.sub || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            sector: {
                              ...formData.sector,
                              sub: e.target.value
                            }
                          })}
                          className="w-full bg-white border-2 border-black rounded-lg px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Sélectionner une sous-catégorie</option>
                          {BUSINESS_SECTORS
                            .find(s => s.value === formData.sector?.main)
                            ?.subcategories.map((subsector) => (
                              <option key={subsector.value} value={subsector.value}>
                                {subsector.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full bg-white border-2 border-black text-gray-900"
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Contacts
                      </label>
                      <button
                        type="button"
                        onClick={handleAddContact}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    {formData.contacts?.map((contact, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border-2 border-black mb-3">
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="text"
                            value={contact.name}
                            onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                            placeholder="Nom"
                            className="w-full bg-white border-2 border-black text-gray-900"
                            required
                          />
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                            placeholder="Email"
                            className="w-full bg-white border-2 border-black text-gray-900"
                            required
                          />
                          <input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                            placeholder="Téléphone"
                            className="w-full bg-white border-2 border-black text-gray-900"
                          />
                          <select
                            value={contact.type}
                            onChange={(e) => handleContactChange(index, 'type', e.target.value)}
                            className="w-full bg-white border-2 border-black text-gray-900"
                          >
                            <option value="brand">Marque</option>
                            <option value="agency">Agence</option>
                          </select>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={contact.role || ''}
                              onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                              placeholder="Rôle (optionnel)"
                              className="w-full bg-white border-2 border-black text-gray-900"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveContact(index)}
                          className="mt-2 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-black rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border-2 border-black rounded-lg hover:bg-indigo-700"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-md w-full mx-4 p-6 shadow-lg">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900">Confirmer la suppression</h3>
              <p className="mt-2 text-sm text-gray-600">
                Êtes-vous sûr de vouloir supprimer le client "{client?.brand_name}" ? Cette action est irréversible.
              </p>
              {collabs.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Attention :</strong> Ce client est associé à {collabs.length} collaboration(s). 
                    La suppression n'affectera pas les collaborations existantes.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}